import * as Popover from '@radix-ui/react-popover'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { Link2, RefreshCcw } from 'lucide-react'
import { type ChangeEvent, useCallback } from 'react'
import { getCustomizerWidth } from '#app/components/tiptap/hooks/useCustomizer.js'
import { Button } from '#app/components/ui/button.js'
import { Input } from '#app/components/ui/input.js'
import { cn } from '#app/utils/misc.js'
import { useUploader } from '../../imageupload/view/hooks'

export const blockVariables = {
	none: 'Custom link',
	confirmationLink: 'Confirmation link',
	referralLink: 'Referral link',
}

export const ImageBlockView = (props: NodeViewProps) => {
	const { editor, getPos, node } = props
	const { src } = node.attrs
	const maxWidth = getCustomizerWidth(props)

	const onUpload = useCallback(
		(url: string) => {
			if (url) {
				props.updateAttributes({ src: url })
			}
		},
		[props],
	)

	const { loading, uploadFile } = useUploader({ onUpload })

	const wrapperClassName = cn(
		node.attrs.align === 'left' ? 'ml-0' : 'ml-auto',
		node.attrs.align === 'right' ? 'mr-0' : 'mr-auto',
		node.attrs.align === 'center' && 'mx-auto',
		'block cursor-pointer',
	)

	const onClick = useCallback(() => {
		editor.commands.setNodeSelection(getPos())
	}, [getPos, editor.commands])

	const handleLinkChange = (link: string) => {
		props.updateAttributes({ link })
	}

	// const handleAlignment = (align: string) => {
	// 	props.updateAttributes({ align })
	// }

	const handleWidth = (width: string) => {
		props.updateAttributes({ width })
	}

	const handleRadius = (radius: string) => {
		props.updateAttributes({ radius })
	}

	const onFileChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			e.target.files ? uploadFile(e.target.files[0]) : null,
		[uploadFile],
	)

	return (
		<NodeViewWrapper
			style={{
				width: String((node.attrs.width / 100) * maxWidth) + 'px',
				maxWidth: '100%',
			}}
		>
			{editor.isEditable ? (
				<Popover.Root>
					<Popover.Trigger asChild>
						<img
							className={`block cursor-pointer ${loading ? 'blur-sm' : 'blur-none'} transition-all duration-200 ease-in-out`}
							style={{ borderRadius: node.attrs.radius + 'px' }}
							src={src}
							alt="some images"
							onClick={onClick}
						/>
					</Popover.Trigger>
					<Popover.Portal>
						<Popover.Content
							className="custom-popover z-50 flex w-[420px] items-center gap-2 rounded-xl border border-border bg-white p-2 shadow-sm"
							sideOffset={8}
							onOpenAutoFocus={e => e.preventDefault()}
						>
							<div className="flex w-full items-center gap-2">
								<label
									className="relative w-full"
									data-tooltip="Add link"
									data-tooltip-direction="up"
								>
									<Input
										type="text"
										placeholder="Link"
										variant="s"
										icon="Link2"
										value={node.attrs.link}
										onChange={e => handleLinkChange(e.currentTarget.value)}
									/>
								</label>
								<div className="flex h-6 w-[1px] shrink-0 bg-border"></div>
								<label data-tooltip="Width" data-tooltip-direction="up">
									<Input
										type="number"
										max={100}
										min={5}
										variant="s"
										value={node.attrs.width}
										defaultValue={node.attrs.width}
										className="w-[70px]"
										icon="Percent"
										onChange={e => handleWidth(e.currentTarget.value)}
									/>
								</label>
								<label data-tooltip="Radius" data-tooltip-direction="up">
									<Input
										type="number"
										max={99}
										min={0}
										step={4}
										variant="s"
										value={node.attrs.radius}
										defaultValue={node.attrs.radius}
										className="w-16"
										icon="Scan"
										onChange={e => handleRadius(e.currentTarget.value)}
									/>
								</label>
								{/* <Button
									variant="ghost"
									data-tooltip="Align left"
									data-tooltip-direction="up"
									size="s"
									onClick={() => handleAlignment('left')}
									className={`w-8 ${node.attrs.align === 'left' ? 'bg-primary' : 'bg-transparent'}`}
								>
									<AlignHorizontalJustifyStart
										width={16}
										className="shrink-0"
									/>
								</Button>
								<Button
									variant="ghost"
									data-tooltip="Align center"
									data-tooltip-direction="up"
									size="s"
									onClick={() => handleAlignment('center')}
									className={`w-8 ${node.attrs.align === 'center' ? 'bg-primary' : 'bg-transparent'}`}
								>
									<AlignHorizontalJustifyCenter
										width={16}
										className="shrink-0"
									/>
								</Button>
								<Button
									variant="ghost"
									data-tooltip="Align right"
									data-tooltip-direction="up"
									size="s"
									onClick={() => handleAlignment('right')}
									className={`w-8 ${node.attrs.align === 'right' ? 'bg-primary' : 'bg-transparent'}`}
								>
									<AlignHorizontalJustifyEnd width={16} className="shrink-0" />
								</Button> */}
							</div>
							<Button
								asChild
								variant="ghost"
								size="s"
								className="w-8 cursor-pointer"
							>
								<label
									htmlFor="uploadFile"
									data-tooltip="Replace file"
									data-tooltip-direction="up"
								>
									<RefreshCcw width={16} className="shrink-0" />
								</label>
							</Button>
							<input
								id="uploadFile"
								className="hidden h-0 w-0 overflow-hidden opacity-0"
								type="file"
								accept=".jpg, .jpeg, .png, .webp, .gif"
								onChange={onFileChange}
							/>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			) : node.attrs.link !== '' ? (
				<a
					href={node.attrs.link}
					className={wrapperClassName + ' group relative'}
					style={{
						width: String((node.attrs.width / 100) * maxWidth) + 'px',
						maxWidth: '100%',
					}}
					rel="noreferrer"
					target="_blank"
				>
					{node.attrs.link !== '' && (
						<div className="absolute bottom-2 right-2 flex h-5 w-5 items-center justify-center rounded-sm border border-border bg-primary-foreground text-white opacity-0 transition-sm group-hover:opacity-100">
							<Link2 width={12} className="shrink-0 !text-black" />
						</div>
					)}

					<img
						className="block shadow-button"
						src={src}
						alt=""
						onClick={onClick}
						style={{ borderRadius: node.attrs.radius + 'px' }}
					/>
				</a>
			) : (
				<img
					src={src}
					alt=""
					className={wrapperClassName + ' !mb-0 block !cursor-default'}
					style={{
						width: String((node.attrs.width / 100) * maxWidth) + 'px',
						borderRadius: node.attrs.radius + 'px',
						maxWidth: '100%',
					}}
				/>
			)}
		</NodeViewWrapper>
	)
}

export default ImageBlockView
